import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    ViewChild,
    computed,
    input,
    output,
} from '@angular/core'
import { BookingChoiceGroups } from '../../../domain/Availability'
import {
    BookingPaymentTerminology,
    bookingPaymentTerminologyForVenueId,
} from '../../../domain/BookingPaymentTerminology'
import { VenueStorefrontBookingChoiceRowComponent } from '../venue-storefront-booking-choice-row/venue-storefront-booking-choice-row.component'
import type {
    VenueStorefrontBookingChoice,
    VenueStorefrontBookingSearch,
} from '../venue-storefront-booking-flow/venue-storefront-booking-flow.types'

type VenueStorefrontEventChoice = Extract<VenueStorefrontBookingChoice, { kind: 'event' }>
type VenueStorefrontReasonChoice = Extract<VenueStorefrontBookingChoice, { kind: 'reason' }>

@Component({
    selector: 'app-venue-storefront-booking-reason-step',
    standalone: true,
    imports: [VenueStorefrontBookingChoiceRowComponent],
    templateUrl: './venue-storefront-booking-reason-step.component.html',
    styleUrls: ['./venue-storefront-booking-reason-step.component.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenueStorefrontBookingReasonStepComponent implements AfterViewInit {
    groups = input.required<BookingChoiceGroups>()
    gridLayout = input(false)
    search = input.required<VenueStorefrontBookingSearch>()
    paymentTerminology = input<BookingPaymentTerminology>(
        bookingPaymentTerminologyForVenueId()
    )
    choiceSelected = output<VenueStorefrontBookingChoice>()
    @ViewChild('heading', { static: true }) private heading!: ElementRef<HTMLHeadingElement>

    protected readonly eventChoices = computed<VenueStorefrontEventChoice[]>(() => {
        return this.groups().events.map(event => ({ kind: 'event', ...event }))
    })
    protected readonly standardChoice = computed<VenueStorefrontBookingChoice | null>(() => {
        const standardBooking = this.groups().standardBooking
        return standardBooking ? { kind: 'standard', ...standardBooking } : null
    })
    protected readonly reasonsAtIdealTime = computed<VenueStorefrontReasonChoice[]>(() => {
        return this.groups().reasonsAtIdealTime.map(reason => ({ kind: 'reason', ...reason }))
    })
    private readonly reasonsOutsideIdealTime = computed<VenueStorefrontReasonChoice[]>(() => {
        return this.groups().reasonsOutsideIdealTime.map(reason => ({
            kind: 'reason',
            ...reason,
        }))
    })
    protected readonly earlierReasons = computed<VenueStorefrontReasonChoice[]>(() => {
        return this.reasonsOutsideIdealTime().filter(choice => {
            return choice.reason.isScheduledEarlierThan(this.search().idealTime)
        })
    })
    protected readonly laterReasons = computed<VenueStorefrontReasonChoice[]>(() => {
        return this.reasonsOutsideIdealTime().filter(choice => {
            return !choice.reason.isScheduledEarlierThan(this.search().idealTime)
        })
    })

    ngAfterViewInit() {
        this.heading.nativeElement.focus({ preventScroll: true })
    }
}
